import {
  Button,
  Chip,
  colors,
  Container,
  IconButton,
  Pagination,
  Tooltip,
  useTheme,
  Zoom,
} from "@mui/material";
import { useEffect } from "react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  chipColorStyles,
  debounce,
  minifyAddress,
  stringToColor,
} from "../support/utils";
import { useMemo } from "react";
import { Grid2, Paper, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useAtom } from "jotai";
import { promptAtom } from "../support/atoms";
import { useMutation, useQuery } from "urql";
import {
  createNameTagMutation,
  deleteNameTagMutation,
  nameTagsQuery,
  updateNameTagMutation,
} from "../support/mutations";
import { useNotification } from "../hooks/useNotification";
import { TransitionGroup } from "react-transition-group";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import ClickToCopy from "./ClickToCopy";

const SaveAddressPrompt = ({ id, name, address, networkId }) => {
  const [input, setInput] = useState({
    id: id,
    address: address,
    name: name,
    networkId: networkId || 1,
  });
  const [, createNameTag] = useMutation(createNameTagMutation);
  const [, updateNameTag] = useMutation(updateNameTagMutation);
  const { showNotification } = useNotification();
  const [, setPrompt] = useAtom(promptAtom);

  return (
    <Grid2 container spacing={2} sx={{ marginTop: 1 }}>
      <Grid2 size={{ xs: 12, sm: 8, md: 7, lg: 7 }}>
        <TextField
          sx={{ width: "100%" }}
          size="small"
          variant="outlined"
          label="Address"
          value={input.address}
          onChange={(e) => {
            setInput((prev) => ({ ...prev, address: e.target.value }));
          }}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 4, md: 5, lg: 5 }}>
        <TextField
          sx={{ width: "100%" }}
          size="small"
          variant="outlined"
          label="Name"
          value={input.name}
          onChange={(e) => {
            setInput((prev) => ({ ...prev, name: e.target.value }));
          }}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} alignContent={"center"}>
        <Grid2 container spacing={1} sx={{ marginTop: 1 }}>
          <Grid2 size={"grow"}></Grid2>
          <Grid2 size={"auto"} alignContent={"center"}>
            <Button
              sx={{ width: 70 }}
              variant="outlined"
              onClick={async () => {
                await (id ? updateNameTag : createNameTag)(
                  { input },
                  { additionalTypenames: ["NameTagConnection"] }
                ).then((res) => {
                  if (res.error)
                    showNotification(res.error.toString(), {
                      severity: "error",
                      autoHideDuration: 3000,
                    });
                  else {
                    showNotification("address " + (id ? "updated" : "saved"), {
                      severity: "success",
                      autoHideDuration: 3000,
                    });
                    setPrompt((prev) => ({ ...prev, isOpen: false }));
                  }
                });
              }}
            >
              Save
            </Button>
          </Grid2>
          <Grid2 size={"auto"} alignContent={"center"}>
            <Button
              sx={{ width: 70 }}
              variant="outlined"
              color="default"
              onClick={() => setPrompt((prev) => ({ ...prev, isOpen: false }))}
            >
              Cancel
            </Button>
          </Grid2>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

const ConfirmDeleteNameTagPrompt = ({ id, refetchNameTags }) => {
  const [, deleteNameTag] = useMutation(deleteNameTagMutation);
  const { showNotification } = useNotification();
  const [, setPrompt] = useAtom(promptAtom);

  return (
    <Grid2 container spacing={1} sx={{ marginTop: 1 }}>
      <Grid2 size={"grow"}></Grid2>
      <Grid2 size={"auto"} alignContent={"center"}>
        <Button
          sx={{ width: 70 }}
          variant="outlined"
          onClick={async () => {
            await deleteNameTag({ id }).then((res) => {
              if (res.error || !res.data.deleteNameTag) {
                showNotification(
                  res.error ? res.error.toString() : "failed to delete address",
                  {
                    severity: "error",
                    autoHideDuration: 3000,
                  }
                );
                setPrompt((prev) => ({ ...prev, isOpen: false }));
              } else {
                refetchNameTags({ requestPolicy: "network-only" });
                showNotification("address deleted", {
                  severity: "success",
                  autoHideDuration: 3000,
                });
                setPrompt((prev) => ({ ...prev, isOpen: false }));
              }
            });
          }}
        >
          Delete
        </Button>
      </Grid2>
      <Grid2 size={"auto"} alignContent={"center"}>
        <Button
          sx={{ width: 70 }}
          variant="outlined"
          color="default"
          onClick={() => setPrompt((prev) => ({ ...prev, isOpen: false }))}
        >
          Cancel
        </Button>
      </Grid2>
    </Grid2>
  );
};

const AddressBookFilter = ({ filters, setFilters }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [minSupplyPercent, setMinSupplyPercent] = useState(null);
  const [, setPrompt] = useAtom(promptAtom);

  useEffect(() => {
    setFilters((prev) => {
      let query = searchParams.get("query");
      if (query) {
        setQuery(query);
        prev.query = query;
        prev.includeAll = true;
      }

      let minSupplyPercent = searchParams.get("minSupplyPercent");
      if (minSupplyPercent) {
        setMinSupplyPercent(minSupplyPercent);
        prev.minSupplyPercent = minSupplyPercent;
      }

      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchParams((prev) => {
      if (query) prev.set("query", query);
      else prev.delete("query");
      if (minSupplyPercent) prev.set("minSupplyPercent", minSupplyPercent);
      else prev.delete("minSupplyPercent");
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, minSupplyPercent]);

  const setQueryDebounced = useMemo(
    () =>
      debounce((e) => {
        setFilters((prev) => ({
          ...prev,
          query: e.target.value,
          includeAll: e.target.value !== "",
        }));
      }, 500),
    [setFilters]
  );

  return (
    <Paper elevation={5} sx={{ marginBottom: 0 }}>
      <Grid2
        container
        sx={{ padding: 2, marginBottom: 1, width: "100%" }}
        spacing={2}
      >
        <Grid2 size={{ xs: 7, sm: 7, ms: 7, lg: 4, xl: 3 }}>
          <TextField
            sx={{ width: "100%" }}
            size="small"
            variant="outlined"
            label="Search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setQueryDebounced(e);
            }}
          />
        </Grid2>
        <Grid2 size={"grow"}></Grid2>
        <Grid2 sx={{ display: "grid" }} size={"auto"}>
          <Button
            sx={{ width: 75 }}
            endIcon={<AddIcon />}
            color="info"
            variant="outlined"
            onClick={() =>
              setPrompt({
                isOpen: true,
                title: "Add new address",
                component: <SaveAddressPrompt />,
              })
            }
          >
            New
          </Button>
        </Grid2>
      </Grid2>
    </Paper>
  );
};

const TypeIcon = ({ type }) => {
  let child;
  switch (type) {
    case "DEX":
      child = (
        <CurrencyExchangeIcon sx={{ color: colors.pink[800], height: 20 }} />
      );
      break;
    case "CEX":
      child = (
        <AccountBalanceIcon sx={{ color: colors.purple[800], height: 20 }} />
      );
      break;
    case "CONTRACT":
      child = <DescriptionIcon sx={{ color: colors.blue[800], height: 20 }} />;
      break;
    case "ERC20":
      child = (
        <MonetizationOnIcon sx={{ color: colors.yellow[800], height: 20 }} />
      );
      break;
    default:
      child = (
        <AccountBalanceWalletIcon
          sx={{ color: colors.green[800], height: 20 }}
        />
      );
  }
  return (
    <Tooltip title={`${type.toLowerCase()}`} sx={{ display: "grid" }}>
      {child}
    </Tooltip>
  );
};
const AddressBook = () => {
  let items = 25;
  const [, setPrompt] = useAtom(promptAtom);
  const [page, setPage] = useState(1);
  const theme = useTheme();
  const handleChange = (event, value) => {
    setPage((prev) => {
      if (value === 1)
        setPageVariables({
          first: items,
          after: "",
        });
      else
        setPageVariables({
          first: items,
          after: btoa("cursor:offset:" + String(value * items - items)),
        });
      return value;
    });
  };
  const [pageVariables, setPageVariables] = useState({
    first: items,
    after: "",
  });
  const [filters, setFilters] = useState({ query: "" });
  const [{ data, error }, refetchNameTags] = useQuery({
    query: nameTagsQuery,
    variables: {
      pageArgs: pageVariables,
      filter: filters,
    },
  });
  const nameTags = data?.nameTags;

  useEffect(() => {
    setPageVariables({
      first: items,
      after: "",
    });
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <Container fixed sx={{ marginTop: 2 }}>
      <AddressBookFilter filters={filters} setFilters={setFilters} />
      {error && <p>Oh no... {error.message}</p>}
      {/* {fetching && <p>Loading...</p>} */}
      {nameTags && (
        <>
          {nameTags.count > items && nameTags.edges.length > 19 && (
            <div style={{ justifyContent: "center", display: "flex" }}>
              <Pagination
                sx={{ marginBottom: 2 }}
                count={
                  parseInt(nameTags.count / items) +
                  (nameTags.count % items === 0 ? 0 : 1)
                }
                page={page}
                onChange={handleChange}
              />
            </div>
          )}
          <TransitionGroup>
            {nameTags.edges.map(({ node }) => {
              const tagColor = stringToColor(node.address, theme);
              return (
                <Zoom key={node.id}>
                  <Paper elevation={5} sx={{ marginBottom: 1 }}>
                    <Grid2
                      container
                      sx={{ width: "100%", padding: 1 }}
                      alignItems="center"
                    >
                      <Grid2
                        size={{ xs: 4, sm: 5, md: 6 }}
                        sx={{ paddingLeft: 1 }}
                      >
                        <Chip
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            ...chipColorStyles(theme, tagColor),
                          }}
                          label={node.name}
                          variant="outlined"
                        />
                      </Grid2>
                      <Grid2
                        size={"grow"}
                        sx={{
                          paddingLeft: 1,
                          paddingRight: 1,
                          display: "grid",
                        }}
                      >
                        <ClickToCopy
                          content={node.address}
                          link={`${node.network.explorer}/address/${node.address}`}
                          label={minifyAddress(node.address)}
                          variant="subtitle2"
                          width={135}
                          tooltip={node.address}
                        />
                      </Grid2>
                      <Grid2 size={"auto"} sx={{ display: "grid" }}>
                        <TypeIcon type={node.type} />
                      </Grid2>
                      <Grid2 size={"auto"} sx={{ display: "grid" }}>
                        <IconButton
                          sx={{ width: 25, height: 25 }}
                          variant="contained"
                          disabled={!node.editable}
                          onClick={async () => {
                            setPrompt({
                              isOpen: true,
                              title: `Update ${node.name}?`,
                              component: (
                                <SaveAddressPrompt
                                  id={node.id}
                                  name={node.name}
                                  address={node.address}
                                  networkId={node.network.id}
                                />
                              ),
                            });
                          }}
                        >
                          <EditIcon sx={{ width: 20, height: 20 }} />
                        </IconButton>
                      </Grid2>
                      <Grid2 size={"auto"} sx={{ display: "grid" }}>
                        <IconButton
                          sx={{ width: 25, height: 25 }}
                          variant="contained"
                          color="error"
                          disabled={!node.editable}
                          onClick={async () => {
                            setPrompt({
                              isOpen: true,
                              maxWidth: "xs",
                              title: `Delete ${node.name}?`,
                              component: (
                                <ConfirmDeleteNameTagPrompt
                                  id={node.id}
                                  refetchNameTags={refetchNameTags}
                                />
                              ),
                            });
                          }}
                        >
                          <DeleteIcon sx={{ width: 20, height: 20 }} />
                        </IconButton>
                      </Grid2>
                    </Grid2>
                  </Paper>
                </Zoom>
              );
            })}
          </TransitionGroup>
          {nameTags.count > items && (
            <div style={{ justifyContent: "center", display: "flex" }}>
              <Pagination
                sx={{ marginBottom: 2 }}
                count={
                  parseInt(nameTags.count / items) +
                  (nameTags.count % items === 0 ? 0 : 1)
                }
                page={page}
                onChange={handleChange}
              />
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default AddressBook;
